import type { LocaleCode } from '@/i18n/locales'
import { useDiagnosticsStore } from '@/store/diagnosticsStore'
import { useSessionStore } from '@/store/sessionStore'
import { Microphone, queryMicPermission } from './audio/microphone'
import type { MicPermission } from './audio/microphone'
import { DEFAULT_PERSONA } from '@/persona/personas'
import type { PersonaId } from '@/persona/personas'
import { createApiClient } from '@/api/client'
import type { ApiClient } from '@/api/client'
import type { ProfileContext } from '@namu/protocol'
import { MockTransport } from './transports/MockTransport'
import { RealtimeTransport } from './transports/RealtimeTransport'
import { WebSpeechTransport } from './transports/WebSpeechTransport'
import { getSpeechRecognitionConstructor, getSpeechSynthesis } from './transports/webSpeechApi'
import type {
  ListenOptions,
  ListenResult,
  SpeakOptions,
  SpeakResult,
  VoiceEvent,
  VoiceTransport,
} from './types'
import { VoiceError } from './types'

/**
 * The one object that owns a live voice session.
 *
 * A singleton rather than a hook, because the session has to survive a route
 * change. Onboarding finishes and the app navigates to the session screen; the
 * microphone, the audio context and the recogniser must not be torn down and
 * rebuilt in between. On a phone, re-acquiring the microphone costs the better
 * part of a second of audio-route negotiation, and it can pop a second
 * permission prompt.
 *
 * The screens compose its primitives rather than being handed a canned flow,
 * because a first run and a returning visit want genuinely different sequences.
 */

export type VoiceMode = 'auto' | 'mock' | 'web-speech' | 'realtime'

export interface PrepareOptions {
  mode?: VoiceMode
  locale: LocaleCode
  rate?: number
  /** Which persona answers. Read from Settings by whoever opens the session. */
  persona?: PersonaId
  /** What the device knows about the person, sent up as the model's context. */
  profile?: ProfileContext | undefined
}

/**
 * Picks a transport.
 *
 * `auto` prefers Namu's own server, because that is the product: a model that
 * hears you hesitate and can be interrupted. It falls back to the browser's
 * speech stack, and then to the deterministic mock, so the app is always
 * usable rather than showing an unsupported-browser wall.
 *
 * The fallback is not a formality. The browser path cannot listen while it
 * speaks and cannot be interrupted, so it is a worse product rather than an
 * equivalent one, and the diagnostics screen says which is running.
 */
function chooseMode(requested: VoiceMode | undefined, canReachServer: boolean): ResolvedMode {
  if (requested === 'mock' || requested === 'web-speech' || requested === 'realtime') {
    return requested
  }
  if (canReachServer) return 'realtime'
  const hasSpeech = getSpeechRecognitionConstructor() !== null || getSpeechSynthesis() !== null
  return hasSpeech ? 'web-speech' : 'mock'
}

export type ResolvedMode = 'mock' | 'web-speech' | 'realtime'

/**
 * Where the server is, if it has been configured.
 *
 * Absent in a build with no API URL, which is how the app stays runnable with
 * nothing but a browser. Read once at module load because a build-time
 * constant cannot change while the page is open.
 */
const API_URL: string = (import.meta.env.VITE_API_URL as string | undefined) ?? ''

/** Reads ?voice=mock from the URL. Used by the end-to-end tests and demos. */
export function modeFromLocation(): VoiceMode {
  if (typeof window === 'undefined') return 'auto'
  const requested = new URLSearchParams(window.location.search).get('voice')
  return requested === 'mock' || requested === 'web-speech' || requested === 'realtime'
    ? requested
    : 'auto'
}

class VoiceSessionController {
  private transport: VoiceTransport | null = null
  private microphone: Microphone | null = null
  private unsubscribeTransport: (() => void) | null = null
  private locale: LocaleCode = 'en'
  private persona: PersonaId = DEFAULT_PERSONA
  private activeMode: ResolvedMode = 'mock'
  private api: ApiClient | null = null
  private resumeGate: { promise: Promise<void>; release: () => void } | null = null
  private readonly answerWatchers = new Set<() => void>()
  /** True once the person has said something the assistant has not answered. */
  private awaitingAnswer = false

  get current(): VoiceTransport | null {
    return this.transport
  }

  /**
   * The API client, shared with anything else that has to reach the server.
   *
   * Public because onboarding's profile writer needs one, and it must be this
   * one. `createApiClient` collapses a concurrent burst into a single
   * registration, but that de-duplication is per instance: a second client
   * built alongside this one registers a second device on a cold start, and
   * the person ends up with two accounts and whichever history won the race.
   * Null in a build with no API URL, which is how the app runs offline.
   */
  get client(): ApiClient | null {
    if (API_URL === '') return null
    this.api ??= createApiClient({ baseUrl: API_URL })
    return this.api
  }

  get mode(): ResolvedMode {
    return this.activeMode
  }

  /**
   * True when the assistant produces its own replies.
   *
   * This decides who runs the open conversation, and only that. With a duplex
   * transport there is no reply for the app to compose, so the loop that
   * listens and answers must not run at all. It deliberately says nothing about
   * whether onboarding runs: the first interview is the device's own work on
   * every transport, because it fills slots the profile owns and no model on
   * the other end can be asked to fill them.
   *
   * Read from the transport rather than from the server's `ready` message,
   * because it is a fact about this client's own code: whether `speak` and
   * `listen` do anything here. What the server reports is a different fact,
   * which is which model is answering.
   */
  get isDuplex(): boolean {
    return this.transport?.capabilities.duplex === true
  }

  /** The language the transport is currently configured for. */
  get activeLocale(): LocaleCode {
    return this.locale
  }

  get isPrepared(): boolean {
    return this.transport !== null
  }

  get hasMicrophone(): boolean {
    return this.microphone?.isOpen === true
  }

  /**
   * Builds the transport and starts routing its events into the store.
   * Deliberately does not touch the microphone: the app speaks first, and only
   * then asks to listen.
   */
  async prepare(options: PrepareOptions): Promise<VoiceTransport> {
    const store = useSessionStore.getState()
    if (this.transport) await this.teardown()

    this.locale = options.locale
    if (options.persona) this.persona = options.persona
    this.activeMode = chooseMode(options.mode, API_URL !== '')
    this.awaitingAnswer = false

    store.reset()
    store.send('START')

    // The microphone object exists from the start so the transport can meter
    // through it the moment permission arrives, but nothing is opened yet.
    this.microphone = new Microphone()

    let transport: VoiceTransport
    if (this.activeMode === 'realtime') {
      this.api ??= createApiClient({ baseUrl: API_URL })
      transport = new RealtimeTransport({
        api: this.api,
        // Handed over once the microphone opens. It is deliberately not opened
        // here: the browser will only grant it inside a user gesture, and this
        // runs before the person has touched anything.
        stream: null,
        profile: options.profile,
      })
    } else if (this.activeMode === 'mock') {
      transport = new MockTransport()
    } else {
      transport = new WebSpeechTransport({ microphone: this.microphone })
    }
    this.transport = transport

    this.unsubscribeTransport = transport.subscribe((event) => {
      useSessionStore.getState().applyVoiceEvent(event)
      this.noteAnswer(event)
      if (event.type === 'speech_end' && event.speaker === 'namu' && event.interrupted) {
        useDiagnosticsStore.getState().recordInterruption()
      }
    })

    try {
      await transport.connect({
        locale: options.locale,
        rate: options.rate ?? 1,
        persona: this.persona,
      })
    } catch (error) {
      // connect() already emitted the error, which the store turned into a
      // banner. Rethrowing here would make every caller write the same
      // try/catch to say nothing new.
      if (!(error instanceof VoiceError)) throw error
    }

    useSessionStore.getState().setCapabilities(transport.capabilities)
    this.exposeTestHooks(transport)
    return transport
  }

  /**
   * In mock mode only, hands the end-to-end tests a way to put words in the
   * user's mouth. There is no real microphone to drive from a test runner, and
   * WebKit cannot fake microphone input at all, so a deterministic seam is the
   * only way to exercise a whole conversation.
   *
   * Gated on the mock transport, so it never exists in a real session.
   */
  private exposeTestHooks(transport: VoiceTransport): void {
    if (typeof window === 'undefined') return
    const scoped = window as unknown as { __namu?: TestBridge }
    if (!(transport instanceof MockTransport)) {
      delete scoped.__namu
      return
    }
    scoped.__namu = {
      say(text: string) {
        transport.enqueueUserSpeech(text, { delayMs: 40 })
      },
    }
  }

  /**
   * Asks for the microphone. Must be called from a user gesture: iOS will not
   * start an AudioContext any other way, and a context that starts suspended
   * reports a flat zero level forever with no error to explain it.
   *
   * Returns whether listening is now possible. A false here is not a dead end,
   * it means the typed path carries the conversation.
   */
  async openMicrophone(): Promise<boolean> {
    const store = useSessionStore.getState()
    if (this.activeMode === 'mock') {
      // The mock hears whatever the test or the typed panel feeds it, so there
      // is nothing to ask for.
      store.send('REQUEST_PERMISSION')
      store.send('PERMISSION_GRANTED')
      return true
    }

    if (!this.microphone) return false
    if (this.microphone.isOpen) return true

    store.send('REQUEST_PERMISSION')
    try {
      await this.microphone.open()
    } catch (error) {
      const voiceError =
        error instanceof VoiceError
          ? error
          : new VoiceError('unknown', 'The microphone could not be opened.', { cause: error })
      store.applyVoiceEvent({ type: 'error', error: voiceError })
      store.send('PERMISSION_DENIED')
      return false
    }

    store.send('PERMISSION_GRANTED')

    /*
     * Permission and capture are caught separately because they fail for
     * unrelated reasons and a person can only act on one of them. Folding both
     * into one handler is how a build where the audio worklet could not load
     * came to tell people their microphone had been refused, and offered them a
     * retry button that could never have worked.
     *
     * The realtime transport borrows this stream rather than opening its own. A
     * second `getUserMedia` on a phone can steal the first one's audio route,
     * which leaves the level meter reading silence while capture hears
     * perfectly well, and it can prompt for permission twice.
     */
    if (this.transport instanceof RealtimeTransport) {
      try {
        // Awaited, because this is what actually starts capture. Firing it and
        // moving on would return "granted" to a caller that then begins the
        // conversation before anything is listening.
        await this.transport.setStream(this.microphone.mediaStream)
      } catch (error) {
        store.applyVoiceEvent({
          type: 'error',
          error: new VoiceError('unsupported', 'Audio could not be started on this browser.', {
            cause: error,
          }),
        })
        return false
      }
    }

    // Capabilities can change once a real stream exists.
    if (this.transport) store.setCapabilities(this.transport.capabilities)
    return true
  }

  /** Current permission state, without triggering a prompt. */
  async permission(): Promise<MicPermission> {
    if (this.activeMode === 'mock') return 'granted'
    return queryMicPermission()
  }

  speak(text: string, options?: SpeakOptions): Promise<SpeakResult> {
    if (!this.transport) return Promise.resolve({ completed: false, durationMs: 0 })
    return this.transport.speak(text, options)
  }

  listen(options?: ListenOptions): Promise<ListenResult> {
    if (!this.transport) return Promise.resolve({ status: 'aborted' })
    return this.transport.listen(options)
  }

  cancelSpeech(): void {
    this.transport?.cancelSpeech()
  }

  endTurn(): void {
    this.transport?.endTurn()
  }

  submitTyped(text: string): void {
    this.transport?.submitTyped(text)
    useDiagnosticsStore.getState().recordTypedFallback()
  }

  /**
   * Feeds an answer the person gave by tapping a labelled choice, such as the
   * consent buttons. It reaches the conversation the same way a typed answer
   * does, but it is not counted as a fallback: choosing a visible option is
   * the intended path for that question, not a sign that speaking failed.
   */
  answerWithChoice(text: string): void {
    this.transport?.submitTyped(text)
  }

  setMuted(muted: boolean): void {
    this.transport?.setMuted(muted)
    this.microphone?.setMuted(muted)
    useSessionStore.getState().setMuted(muted)
  }

  /**
   * Hold suspends the exchange. Distinct from mute, which only closes the
   * microphone while Namu keeps talking. Collapsing the two would force
   * someone who wants to stop the assistant to also give up their own turn.
   *
   * Holding does end the open turn, because the microphone is about to close.
   * What it must not do is let that ended turn look like the person going
   * quiet: the conversation loops wait on `whenResumed()` and give the attempt
   * back. Without the gate, pressing Pause spoke "Are you still there?" out
   * loud, skipped the question, and hung up about fifteen seconds later.
   */
  setHeld(held: boolean): void {
    if (held) {
      if (!this.resumeGate) {
        let release = () => {}
        const promise = new Promise<void>((resolve) => {
          release = resolve
        })
        this.resumeGate = { promise, release }
      }
      this.transport?.cancelSpeech()
      this.transport?.endTurn()
      this.microphone?.setMuted(true)
    } else {
      this.resumeGate?.release()
      this.resumeGate = null
      this.microphone?.setMuted(useSessionStore.getState().muted)
    }
    useSessionStore.getState().setHeld(held)
  }

  get isHeld(): boolean {
    return this.resumeGate !== null
  }

  /** Resolves immediately unless the conversation is on hold. */
  whenResumed(): Promise<void> {
    return this.resumeGate?.promise ?? Promise.resolve()
  }

  setLocale(locale: LocaleCode): void {
    this.locale = locale
    this.transport?.setLocale(locale)
    if (this.transport) useSessionStore.getState().setCapabilities(this.transport.capabilities)
  }

  /**
   * Change who answers, from Settings, while a conversation may be open.
   *
   * Held here as well as pushed down, because a session prepared after this
   * call and before the next read of Settings would otherwise open with the
   * persona the person has just moved away from.
   */
  setPersona(persona: PersonaId): void {
    this.persona = persona
    this.transport?.setPersona(persona)
  }

  /** Who is answering now. Read by Settings and by the diagnostics screen. */
  get activePersona(): PersonaId {
    return this.persona
  }

  setRate(rate: number): void {
    this.transport?.setRate(rate)
  }

  /** Resumes an audio context the OS suspended while the app was backgrounded. */
  async resumeAudio(): Promise<void> {
    await this.microphone?.resume()
  }

  /**
   * Calls back every time the assistant finishes answering something the person
   * said, and returns the way to stop listening.
   *
   * This exists because on a duplex session the app is not the author of the
   * reply and so cannot inspect it. What it can still observe is the shape of
   * the exchange, which is all the caller needs: onboarding earns the right to
   * ask where somebody lives once Namu has been useful to them, and "the person
   * spoke, and the assistant answered them all the way to the end" is that
   * moment as seen from outside the words.
   *
   * Three parts of the rule are each a specific wrong answer avoided. It waits
   * for the person to speak first, or the opening greeting would count as an
   * answer to a question nobody asked. It ignores an interrupted turn, because
   * an answer talked over is one the person did not want. And it re-arms only
   * on the next thing the person says, so one turn cannot be counted twice by a
   * transport that reports the end of speech more than once.
   */
  watchAnswers(listener: () => void): () => void {
    this.answerWatchers.add(listener)
    return () => {
      this.answerWatchers.delete(listener)
    }
  }

  private noteAnswer(event: VoiceEvent): void {
    if (event.type === 'transcript' && event.speaker === 'user' && event.final) {
      // Whitespace is not a turn. A recogniser that closes an empty turn would
      // otherwise arm the next thing the assistant says.
      if (event.text.trim() !== '') this.awaitingAnswer = true
      return
    }

    if (event.type !== 'speech_end' || event.speaker !== 'namu') return
    if (event.interrupted || !this.awaitingAnswer) return

    this.awaitingAnswer = false
    for (const watcher of this.answerWatchers) watcher()
  }

  /**
   * Resolves when the session is over.
   *
   * Only meaningful for a duplex transport, where nothing in the app is
   * driving the conversation and there is otherwise nothing to await. It
   * watches the state machine rather than the socket, so ending the session
   * from the End button, from the server closing, or from the abort signal all
   * settle it the same way.
   */
  whenEnded(signal?: AbortSignal): Promise<void> {
    return new Promise<void>((resolve) => {
      if (signal?.aborted) {
        resolve()
        return
      }

      const finish = (): void => {
        unsubscribe()
        signal?.removeEventListener('abort', finish)
        resolve()
      }

      const unsubscribe = useSessionStore.subscribe((state) => {
        if (state.state === 'ended' || state.state === 'idle') finish()
      })

      signal?.addEventListener('abort', finish, { once: true })
    })
  }

  async teardown(): Promise<void> {
    const store = useSessionStore.getState()

    // Diagnostics keep a copy of the rejection count before the machine that
    // holds it is reset, since a burst of them is the signature of a transport
    // emitting events out of order.
    useDiagnosticsStore.getState().recordRejectedTransitions(store.rejectedTransitions().length)

    this.unsubscribeTransport?.()
    this.unsubscribeTransport = null

    // Release anything waiting on the hold gate, or teardown deadlocks behind
    // a conversation that will never resume.
    this.resumeGate?.release()
    this.resumeGate = null

    // The watchers belong to the screen and are removed by it. What must not
    // survive is the half-finished exchange, or the first thing the assistant
    // says in the next session answers something said in this one.
    this.awaitingAnswer = false

    try {
      await this.transport?.disconnect()
    } catch {
      // A transport that fails to shut down cleanly must not block teardown.
    }
    this.transport = null

    this.microphone?.close()
    this.microphone = null

    // The test hook belongs to the session, not to the page. Leaving it behind
    // means a stale handle to a torn-down transport that quietly does nothing.
    if (typeof window !== 'undefined') {
      delete (window as unknown as { __namu?: unknown }).__namu
    }

    store.send('END')
  }

  /** The room's measured noise floor, for the diagnostics screen. */
  get noiseFloorDb(): number | null {
    return this.microphone?.noiseFloorDb ?? null
  }
}

export const voiceSession = new VoiceSessionController()

/**
 * The seam the end-to-end tests drive a mock conversation through.
 *
 * Two entries, and they are two different kinds of thing a test cannot
 * otherwise produce. `say` is the microphone: there is none to drive from a
 * test runner. `served` is the model: on a real session the assistant's own
 * answers earn the place question, through `watchAnswers`, but the end-to-end
 * suite runs against the mock transport, where every word the assistant says
 * was authored by the app and the on-device provider never returns an `answer`
 * intent. Without this the browser walk could not reach the last question in
 * the flow at all.
 */
export interface TestBridge {
  say(text: string): void
  served?(): void
}

/**
 * Registers the "a question was answered well" signal, in mock mode only.
 *
 * Kept here rather than in the conversation so that the whole of the bridge,
 * including the fact that it is deleted on teardown and never exists outside
 * the mock transport, is described in one file.
 */
export function exposeServedHook(served: () => void): void {
  if (typeof window === 'undefined') return
  if (voiceSession.mode !== 'mock') return
  const bridge = (window as unknown as { __namu?: TestBridge }).__namu
  if (!bridge) return
  bridge.served = served
}
